import React, { useState, useEffect, useRef } from 'react';
import html2pdf from 'html2pdf.js';
import { Link } from 'react-router-dom';

const DuctCalculator2 = () => {
    const [formData, setFormData] = useState({
        clientName: '',
        uvSize: '',
        width: '',
        length: '',
        cfm: '',
        lampLength: '',
        numRows: '',
        numCols: '',
        totalWattage: '',
        at1mWcm2Input: '',
        at450mWcm2Input: ''
    });

    const [calculations, setCalculations] = useState({
        ductAreaMm2: '',
        ductAreaM2: '',
        m3Hr: '',
        m3Min: '',
        m3Sec: '',
        flowVelocity: '',
        exposureTime: '',
        at1mWcm2: '',
        at1Wcm2: '',
        at1mIreqD90Wm2: '',
        at1mNoOfLampsD90: '',
        at1mIreqD99Wm2: '',
        at1mNoOfLampsD99: '',
        at450mWcm2: '',
        at450mmWm2: '',
        iReqD90Wm2: '',
        noOfLampsD90: '',
        iReqD99Wm2: '',
        noOfLampsD99: '',
        correctedUVD: '',
        minimumIntAtD: '',
        intensityAtA: '',
        intensityAtB: ''
    });

    const [conversions, setConversions] = useState({
        uvSizeInCm: '',
        widthInCm: '',
        lengthInCm: '',
        lampLengthInCm: ''
    });

    const [includeDiagrams, setIncludeDiagrams] = useState('yes');
    const [reportType, setReportType] = useState('full');

    const layoutCanvasRef = useRef(null);
    const uvCanvasRef = useRef(null);

    useEffect(() => {
        if (formData.uvSize) {
            setConversions(prev => ({ ...prev, uvSizeInCm: (parseFloat(formData.uvSize) / 10).toFixed(2) }));
        }
    }, [formData.uvSize]);

    useEffect(() => {
        if (formData.width) {
            setConversions(prev => ({ ...prev, widthInCm: (parseFloat(formData.width) / 10).toFixed(2) }));
        }
    }, [formData.width]);

    useEffect(() => {
        if (formData.length) {
            setConversions(prev => ({ ...prev, lengthInCm: (parseFloat(formData.length) / 10).toFixed(2) }));
        }
    }, [formData.length]);

    useEffect(() => {
        if (formData.lampLength) {
            setConversions(prev => ({ ...prev, lampLengthInCm: (parseFloat(formData.lampLength) / 10).toFixed(2) }));
        }
    }, [formData.lampLength]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleReset = () => {
        setFormData({
            clientName: '',
            uvSize: '',
            width: '',
            length: '',
            cfm: '',
            lampLength: '',
            numRows: '',
            numCols: '',
            totalWattage: '',
            at1mWcm2Input: '',
            at450mWcm2Input: ''
        });
        setCalculations({
            ductAreaMm2: '',
            ductAreaM2: '',
            m3Hr: '',
            m3Min: '',
            m3Sec: '',
            flowVelocity: '',
            exposureTime: '',
            at1mWcm2: '',
            at1Wcm2: '',
            at1mIreqD90Wm2: '',
            at1mNoOfLampsD90: '',
            at1mIreqD99Wm2: '',
            at1mNoOfLampsD99: '',
            at450mWcm2: '',
            at450mmWm2: '',
            iReqD90Wm2: '',
            noOfLampsD90: '',
            iReqD99Wm2: '',
            noOfLampsD99: '',
            correctedUVD: '',
            minimumIntAtD: '',
            intensityAtA: '',
            intensityAtB: ''
        });
        setConversions({
            uvSizeInCm: '',
            widthInCm: '',
            lengthInCm: '',
            lampLengthInCm: ''
        });
    };

    const calculate = () => {
        const { width, length, cfm, numRows, numCols, totalWattage, lampLength, at1mWcm2Input, at450mWcm2Input } = formData;

        if (!width || !length || !cfm) {
            alert('Please fill in all required fields');
            return;
        }

        const ductAreaMm2 = parseFloat(width) * parseFloat(length);
        const ductAreaM2 = ductAreaMm2 / 1000000;
        const m3Hr = parseFloat(cfm) * 1.69901082;
        const m3Min = parseFloat(cfm) * 0.0283168;
        const m3Sec = parseFloat(cfm) * 0.000471947;
        const flowVelocity = m3Sec / ductAreaM2;
        const exposureTime = 2 / flowVelocity;

        const at1Wcm2 = parseFloat(at1mWcm2Input) * 10;
        const at1mIreqD90Wm2 = 6 / exposureTime;
        const at1mNoOfLampsD90 = at1mIreqD90Wm2 / at1Wcm2;
        const at1mIreqD99Wm2 = 12 / exposureTime;
        const at1mNoOfLampsD99 = at1mIreqD99Wm2 / at1Wcm2;

        const at450mmWm2 = parseFloat(at450mWcm2Input) * 10;
        const iReqD90Wm2 = 6 / exposureTime;
        const noOfLampsD90 = iReqD90Wm2 / at450mmWm2;
        const iReqD99Wm2 = 12 / exposureTime;
        const noOfLampsD99 = iReqD99Wm2 / at450mmWm2;

        // Calculation 2
        const lengthInCm = parseFloat(length) / 10;
        const widthInCm = parseFloat(width) / 10;
        const lampLengthInCm = parseFloat(lampLength) / 10;

        const ductWidthAlt = widthInCm / (parseFloat(numCols) * 2);
        const ductHeightAlt = lengthInCm / (parseFloat(numRows) * 2);
        const avgIntensityDistance = Math.max(ductHeightAlt, ductWidthAlt) / 1.5;
        const totalAverageIntensity = ((parseFloat(totalWattage) * 0.3) * (Math.log(avgIntensityDistance) + 0.577) * (10 ** 6)) / (2 * Math.PI * lampLengthInCm * avgIntensityDistance);
        const contactTime = ((((lampLengthInCm / 30.48) + 1) * (lengthInCm / 30.48) * (widthInCm / 30.48)) / parseFloat(cfm)) * 60;
        const correctedDosage = totalAverageIntensity * contactTime * 0.7;

        const diagonalDistance = Math.sqrt(ductHeightAlt ** 2 + ductWidthAlt ** 2);
        const minIntensity = (((parseFloat(totalWattage) / (parseFloat(numCols) * parseFloat(numRows))) * 0.3 * 10 ** 6) / (2 * Math.PI * lampLengthInCm * diagonalDistance)) * 0.63;
        const aIntensity = (((parseFloat(totalWattage) / (parseFloat(numCols) * parseFloat(numRows))) * 0.3 * 10 ** 6) / (2 * Math.PI * lampLengthInCm * ductWidthAlt)) * 0.63;
        const bIntensity = (((parseFloat(totalWattage) / (parseFloat(numCols) * parseFloat(numRows))) * 0.3 * 10 ** 6) / (2 * Math.PI * lampLengthInCm * ductHeightAlt)) * 0.63;

        setCalculations({
            ductAreaMm2: ductAreaMm2.toFixed(4),
            ductAreaM2: ductAreaM2.toFixed(4),
            m3Hr: m3Hr.toFixed(4),
            m3Min: m3Min.toFixed(4),
            m3Sec: m3Sec.toFixed(4),
            flowVelocity: flowVelocity.toFixed(2),
            exposureTime: exposureTime.toFixed(4),
            at1mWcm2: at1mWcm2Input,
            at1Wcm2: at1Wcm2.toFixed(4),
            at1mIreqD90Wm2: at1mIreqD90Wm2.toFixed(2),
            at1mNoOfLampsD90: at1mNoOfLampsD90.toFixed(2),
            at1mIreqD99Wm2: at1mIreqD99Wm2.toFixed(2),
            at1mNoOfLampsD99: at1mNoOfLampsD99.toFixed(2),
            at450mWcm2: at450mWcm2Input,
            at450mmWm2: at450mmWm2.toFixed(4),
            iReqD90Wm2: iReqD90Wm2.toFixed(2),
            noOfLampsD90: noOfLampsD90.toFixed(2),
            iReqD99Wm2: iReqD99Wm2.toFixed(2),
            noOfLampsD99: noOfLampsD99.toFixed(2),
            correctedUVD: parseInt(correctedDosage).toFixed(0),
            minimumIntAtD: parseInt(minIntensity).toFixed(0),
            intensityAtA: parseInt(aIntensity).toFixed(0),
            intensityAtB: parseInt(bIntensity).toFixed(0)
        });

        // Draw canvases after calculations
        setTimeout(() => {
            drawUvLayout();
            drawHeatmap();
        }, 100);
    };

    const drawUvLayout = () => {
        const canvas = layoutCanvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const W = 485;
        const H = 250;
        canvas.width = W;
        canvas.height = H;

        ctx.clearRect(0, 0, W, H);

        const marginLeft = 70;
        const marginBottom = 50;
        const marginTop = 30;
        const marginRight = 40;
        const plotW = W - marginLeft - marginRight;
        const plotH = H - marginTop - marginBottom;

        const length = parseFloat(formData.length);
        const width = parseFloat(formData.width);
        const rows = parseInt(formData.numRows) || 1;

        ctx.strokeStyle = '#000';
        ctx.lineWidth = 1;

        // X axis
        ctx.beginPath();
        ctx.moveTo(marginLeft, marginTop + plotH);
        ctx.lineTo(marginLeft + plotW, marginTop + plotH);
        ctx.stroke();

        // Y axis
        ctx.beginPath();
        ctx.moveTo(marginLeft, marginTop);
        ctx.lineTo(marginLeft, marginTop + plotH);
        ctx.stroke();

        ctx.font = '11px Arial';
        ctx.fillStyle = '#000';

        // X ticks
        const xTicks = 10;
        for (let i = 0; i <= xTicks; i++) {
            const x = marginLeft + (i / xTicks) * plotW;
            const val = Math.round((i / xTicks) * length);
            ctx.beginPath();
            ctx.moveTo(x, marginTop + plotH);
            ctx.lineTo(x, marginTop + plotH + 5);
            ctx.stroke();
            ctx.fillText(val, x - 8, marginTop + plotH + 18);
        }
        ctx.fillText('Length (cm)', marginLeft + plotW / 2 - 30, H - 8);

        // Y ticks
        const yTicks = 10;
        for (let i = 0; i <= yTicks; i++) {
            const y = marginTop + plotH - (i / yTicks) * plotH;
            const val = Math.round((i / yTicks) * width);
            ctx.beginPath();
            ctx.moveTo(marginLeft, y);
            ctx.lineTo(marginLeft - 5, y);
            ctx.stroke();
            ctx.fillText(val, marginLeft - 40, y + 4);
        }

        ctx.save();
        ctx.translate(15, marginTop + plotH / 2);
        ctx.rotate(-Math.PI / 2);
        ctx.fillText('Height / Width (cm)', -45, 0);
        ctx.restore();

        // Duct geometry
        const ox = marginLeft;
        const oy = marginTop;
        const ductW = plotW;
        const ductH = plotH;
        const depth = 60;

        // Front Face
        ctx.fillStyle = '#d9d9d9';
        ctx.fillRect(ox, oy, ductW, ductH);

        // Top Face
        ctx.fillStyle = '#e6e6e6';
        ctx.beginPath();
        ctx.moveTo(ox, oy);
        ctx.lineTo(ox + depth, oy - depth);
        ctx.lineTo(ox + ductW + depth, oy - depth);
        ctx.lineTo(ox + ductW, oy);
        ctx.closePath();
        ctx.fill();

        // Side Face
        ctx.fillStyle = '#bfbfbf';
        ctx.beginPath();
        ctx.moveTo(ox + ductW, oy);
        ctx.lineTo(ox + ductW + depth, oy - depth);
        ctx.lineTo(ox + ductW + depth, oy - depth + ductH);
        ctx.lineTo(ox + ductW, oy + ductH);
        ctx.closePath();
        ctx.fill();

        ctx.strokeStyle = '#444';
        ctx.strokeRect(ox, oy, ductW, ductH);

        // UV Plate
        const uvPlateX = ox + ductW * 0.22;
        const uvPlateW = 12;
        ctx.fillStyle = 'rgba(0,150,255,0.35)';
        ctx.fillRect(uvPlateX, oy, uvPlateW, ductH);

        // UV rods
        ctx.strokeStyle = '#ff9800';
        ctx.lineWidth = 4;
        const spacing = ductH / (rows + 1);
        for (let i = 1; i <= rows; i++) {
            const y = oy + i * spacing;
            ctx.beginPath();
            ctx.moveTo(uvPlateX - 2, y);
            ctx.lineTo(uvPlateX + uvPlateW + 2, y);
            ctx.stroke();
        }

        // Arrow and label
        ctx.strokeStyle = '#333';
        ctx.fillStyle = '#333';
        ctx.lineWidth = 2;
        const arrowStartX = uvPlateX + 10;
        const arrowStartY = oy + ductH * 0.80;
        const arrowEndX = uvPlateX + 30;
        const arrowEndY = arrowStartY;

        ctx.beginPath();
        ctx.moveTo(arrowStartX, arrowStartY);
        ctx.lineTo(arrowEndX, arrowEndY);
        ctx.stroke();

        const headSize = 8;
        ctx.beginPath();
        ctx.moveTo(arrowEndX, arrowEndY);
        ctx.lineTo(arrowEndX - headSize, arrowEndY - headSize / 2);
        ctx.lineTo(arrowEndX - headSize, arrowEndY + headSize / 2);
        ctx.closePath();
        ctx.fill();

        ctx.font = '13px Arial';
        ctx.fillText('UVGI System', arrowStartX + 25, arrowStartY + 5);

        // Airflow arrows
        ctx.strokeStyle = '#ff0000';
        ctx.fillStyle = '#ff0000';
        ctx.lineWidth = 2;
        for (let i = 0; i < 3; i++) {
            const y = oy + ductH * (0.3 + i * 0.2);
            drawArrow(ctx, ox - 10, y, uvPlateX - 8, y, 12);
        }

        // Particles
        ctx.fillStyle = 'rgba(255,165,0,0.7)';
        const particleCount = 70;
        for (let i = 0; i < particleCount / 2; i++) {
            ctx.beginPath();
            ctx.arc(ox + Math.random() * (uvPlateX - ox - 20), oy + 15 + Math.random() * (ductH - 30), 2, 0, Math.PI * 2);
            ctx.fill();
        }
        for (let i = 0; i < particleCount / 2; i++) {
            ctx.beginPath();
            ctx.arc(uvPlateX + uvPlateW + Math.random() * (ductW - (uvPlateX - ox) - 40), oy + 15 + Math.random() * (ductH - 30), 2, 0, Math.PI * 2);
            ctx.fill();
        }

        ctx.fillStyle = '#000';
        ctx.font = '12px Arial';
        ctx.fillText('Air Flow', ox + 10, oy + 40);
    };

    const drawArrow = (ctx, x1, y1, x2, y2, size) => {
        const angle = Math.atan2(y2 - y1, x2 - x1);
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(x2, y2);
        ctx.lineTo(x2 - size * Math.cos(angle - Math.PI / 6), y2 - size * Math.sin(angle - Math.PI / 6));
        ctx.lineTo(x2 - size * Math.cos(angle + Math.PI / 6), y2 - size * Math.sin(angle + Math.PI / 6));
        ctx.closePath();
        ctx.fill();
    };

    const drawHeatmap = () => {
        const canvas = uvCanvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const W = 500;
        const H = 350;
        canvas.width = W;
        canvas.height = H;

        ctx.clearRect(0, 0, W, H);

        const numRows = Math.max(1, parseInt(formData.numRows) || 1);
        const minI = parseFloat(calculations.minimumIntAtD || 1);
        const Ia = parseFloat(calculations.intensityAtA || 2000);
        const Ib = parseFloat(calculations.intensityAtB || 800);

        const margin = { left: 80, top: 60, right: 110, bottom: 70 };
        const drawW = W - margin.left - margin.right;
        const drawH = H - margin.top - margin.bottom;

        const rowGap = drawH / numRows;
        const lampLength = Math.max(60, Math.round(drawW * 0.68));
        const lampX = margin.left + (drawW - lampLength) / 2;
        const lampThickness = Math.max(4, Math.round(drawH * 0.02));

        const coilWCm = parseFloat(formData.length || 244);
        const coilHCm = parseFloat(formData.width || 152);

        const rods = [];
        for (let r = 0; r < numRows; r++) {
            const cy = margin.top + rowGap * r + rowGap / 2;
            rods.push({ x1: lampX, x2: lampX + lampLength, y: cy });
        }

        const centerX = margin.left + drawW / 2;
        const centerY = margin.top + drawH / 2;
        const radius = Math.sqrt((drawW / 2) * (drawW / 2) + (drawH / 2) * (drawH / 2));

        const radialBase = (px, py) => {
            const dx = px - centerX;
            const dy = py - centerY;
            const d = Math.sqrt(dx * dx + dy * dy);
            return Math.max(0, 1 - (d / radius));
        };

        const imgW = Math.max(1, Math.round(drawW));
        const imgH = Math.max(1, Math.round(drawH));
        const img = ctx.createImageData(imgW, imgH);
        const data = img.data;

        let maxVal = 0;
        const field = new Float32Array(imgW * imgH);

        const sigmaNear = Math.max(6, Math.min(drawH, rowGap * 0.25));
        const sigmaMid = Math.max(18, Math.min(drawH * 0.8, rowGap * 0.9));

        for (let iy = 0; iy < imgH; iy++) {
            const py = margin.top + (iy + 0.5) / imgH * drawH;
            for (let ix = 0; ix < imgW; ix++) {
                const px = margin.left + (ix + 0.5) / imgW * drawW;
                const rb = radialBase(px, py);
                const base = minI + (Ia - minI) * (0.18 * Math.pow(rb, 0.8));

                let add = 0;
                for (let ri = 0; ri < rods.length; ri++) {
                    const rd = rods[ri];
                    let dx = 0;
                    if (px < rd.x1) dx = rd.x1 - px;
                    else if (px > rd.x2) dx = px - rd.x2;
                    const dy = Math.abs(py - rd.y);
                    const dist = Math.sqrt(dx * dx + dy * dy);

                    add += Ia * Math.exp(-(dist * dist) / (2 * sigmaNear * sigmaNear)) * 0.95;
                    add += Ib * Math.exp(-(dist * dist) / (2 * sigmaMid * sigmaMid)) * 0.45;
                }

                const total = base + add;
                field[iy * imgW + ix] = total;
                if (total > maxVal) maxVal = total;
            }
        }

        const uvColorMap = (v) => {
            v = Math.max(0, Math.min(1, v));
            const stops = [
                { t: 0.00, c: [8, 30, 80] },
                { t: 0.20, c: [16, 64, 140] },
                { t: 0.40, c: [46, 107, 218] },
                { t: 0.60, c: [255, 233, 20] },
                { t: 0.80, c: [254, 164, 83] },
                { t: 1.00, c: [255, 120, 0] }
            ];

            for (let i = 0; i < stops.length - 1; i++) {
                const a = stops[i];
                const b = stops[i + 1];
                if (v >= a.t && v <= b.t) {
                    const t = (v - a.t) / (b.t - a.t);
                    return [
                        Math.round(a.c[0] + (b.c[0] - a.c[0]) * t),
                        Math.round(a.c[1] + (b.c[1] - a.c[1]) * t),
                        Math.round(a.c[2] + (b.c[2] - a.c[2]) * t)
                    ];
                }
            }
            return stops[stops.length - 1].c.slice();
        };

        for (let y = 0; y < imgH; y++) {
            for (let x = 0; x < imgW; x++) {
                const v = field[y * imgW + x];
                const norm = maxVal > 0 ? Math.min(1, v / maxVal) : 0;
                const col = uvColorMap(norm);
                const idx = (y * imgW + x) * 4;
                data[idx] = col[0];
                data[idx + 1] = col[1];
                data[idx + 2] = col[2];
                data[idx + 3] = 255;
            }
        }

        ctx.save();
        ctx.beginPath();
        ctx.rect(margin.left, margin.top, drawW, drawH);
        ctx.clip();

        ctx.fillStyle = '#f8fbfc';
        ctx.fillRect(margin.left, margin.top, drawW, drawH);

        const temp = document.createElement('canvas');
        temp.width = imgW;
        temp.height = imgH;
        temp.getContext('2d').putImageData(img, 0, 0);
        ctx.drawImage(temp, margin.left, margin.top, drawW, drawH);
        ctx.restore();

        // Rod glows
        ctx.save();
        ctx.beginPath();
        ctx.rect(margin.left, margin.top, drawW, drawH);
        ctx.clip();
        ctx.globalCompositeOperation = 'lighter';

        rods.forEach((rd) => {
            const dx = rd.x1 + (rd.x2 - rd.x1) / 2 - centerX;
            const dy = rd.y - centerY;
            const dd = Math.sqrt(dx * dx + dy * dy);
            const centerFactor = 1 - (dd / radius);
            const rodPeak = Ia * (0.85 + 0.25 * centerFactor);
            const rodNorm = Math.max(0, Math.min(1, (rodPeak - minI) / Math.max(1, Ia - minI)));
            const glowR = Math.max(14, Math.round(Math.min(drawW, drawH) * (0.12 + 0.28 * rodNorm)));

            const g = ctx.createRadialGradient(
                rd.x1 + (rd.x2 - rd.x1) / 2, rd.y, 2,
                rd.x1 + (rd.x2 - rd.x1) / 2, rd.y, glowR
            );

            const hot = uvColorMap(1.0);
            const mid = uvColorMap(Math.max(0.25, rodNorm * 0.8));
            const cool = uvColorMap(Math.max(0.05, rodNorm * 0.25));

            g.addColorStop(0.00, `rgba(${hot[0]},${hot[1]},${hot[2]},0.95)`);
            g.addColorStop(0.25, `rgba(${mid[0]},${mid[1]},${mid[2]},0.65)`);
            g.addColorStop(0.65, `rgba(${cool[0]},${cool[1]},${cool[2]},0.25)`);
            g.addColorStop(1.00, 'rgba(20,40,120,0.06)');

            ctx.fillStyle = g;
            ctx.beginPath();
            ctx.arc(rd.x1 + (rd.x2 - rd.x1) / 2, rd.y, glowR, 0, Math.PI * 2);
            ctx.fill();
        });

        ctx.globalCompositeOperation = 'source-over';
        ctx.restore();

        // Draw rods
        ctx.fillStyle = '#80eaff';
        rods.forEach(rd => {
            const rectW = rd.x2 - rd.x1;
            ctx.fillRect(rd.x1, rd.y - lampThickness / 2, rectW, lampThickness);
            ctx.strokeStyle = 'rgba(0,0,0,0.12)';
            ctx.strokeRect(rd.x1, rd.y - lampThickness / 2, rectW, lampThickness);
        });

        // Border
        ctx.strokeStyle = 'rgba(0,0,0,0.18)';
        ctx.lineWidth = 1;
        ctx.strokeRect(margin.left, margin.top, drawW, drawH);

        // Legend
        const barX = margin.left + drawW + 12;
        const barY = margin.top;
        const barW = 22;
        const barH = drawH;

        for (let i = 0; i < barH; i++) {
            const v = 1 - i / (barH - 1);
            const col = uvColorMap(v);
            ctx.fillStyle = `rgb(${col[0]},${col[1]},${col[2]})`;
            ctx.fillRect(barX, barY + i, barW, 1);
        }

        ctx.strokeStyle = '#222';
        ctx.strokeRect(barX, barY, barW, barH);
        ctx.fillStyle = '#000';
        ctx.font = '11px Arial';
        ctx.fillText('High', barX + barW + 6, barY + 12);
        ctx.fillText('Low', barX + barW + 6, barY + barH - 6);

        // Axes
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 1;

        // X axis
        ctx.beginPath();
        ctx.moveTo(margin.left, margin.top + drawH);
        ctx.lineTo(margin.left + drawW, margin.top + drawH);
        ctx.stroke();

        // Y axis
        ctx.beginPath();
        ctx.moveTo(margin.left, margin.top);
        ctx.lineTo(margin.left, margin.top + drawH);
        ctx.stroke();

        // X label
        ctx.font = '12px Arial';
        ctx.fillText('Length (cm)', margin.left + drawW / 2 - 30, margin.top + drawH + 36);

        // Y ticks
        const yTicks = Math.min(10, Math.max(4, Math.round(coilHCm / 25)));
        ctx.font = '10px Arial';

        for (let i = 0; i <= yTicks; i++) {
            const y = margin.top + drawH - (i / yTicks) * drawH;
            const label = Math.round((i / yTicks) * coilHCm);
            ctx.beginPath();
            ctx.moveTo(margin.left, y);
            ctx.lineTo(margin.left - 5, y);
            ctx.stroke();
            ctx.fillText(label, margin.left - 36, y + 3);
        }

        // Y label
        ctx.save();
        ctx.translate(20, margin.top + drawH / 2);
        ctx.rotate(-Math.PI / 2);
        ctx.font = '12px Arial';
        ctx.fillText('Height / Width (cm)', -40, 0);
        ctx.restore();

        // Title
        ctx.font = 'bold 14px Arial';
        ctx.fillText('UVGI System in the above duct diagram', margin.left + drawW / 2 - 90, margin.top - 12);
    };



    // Convert canvas to image (base64)
    const canvasToImage = (canvasRef) => {
        if (!canvasRef?.current) return "";
        return canvasRef.current.toDataURL("image/png", 1.0);
    };



    //     const buildPdfHtml = () => {
    //         const showCalc1 = reportType === "full";
    //         const showDiagrams = includeDiagrams === "yes";

    //         const layoutImg = showDiagrams ? canvasToImage(layoutCanvasRef) : "";
    //         const heatmapImg = showDiagrams ? canvasToImage(uvCanvasRef) : "";

    //         const safe = (v, unit = "") =>
    //             v !== undefined && v !== null && v !== ""
    //                 ? `${Number(v).toFixed(4)} ${unit}`
    //                 : "N/A";

    //         return `
    // <!DOCTYPE html>
    // <html>
    // <head>
    // <meta charset="UTF-8"/>
    // <title>Duct UVGI Result</title>

    // <style>
    //   body {
    //     font-family: Arial, sans-serif;
    //     background: #fff;
    //     margin: 0;
    //     padding: 0;
    //   }

    //   .page {
    //     max-width: 900px;
    //     margin: auto;
    //     padding: 10px;
    //     border: 1px solid #ccc;
    //   }

    //   h2 {
    //     font-size: 16px;
    //     color: #007cff;
    //     margin: 0;
    //   }

    //   h3 {
    //     font-size: 13px;
    //     color: #007cff;
    //     margin: 6px 0;
    //   }

    //   table {
    //     width: 100%;
    //     border-collapse: collapse;
    //     font-size: 11.5px;
    //   }

    //   td {
    //     padding: 4px 6px;
    //     vertical-align: top;
    //   }

    //   .label {
    //     font-weight: bold;
    //     width: 58%;
    //     color: #333;
    //   }

    //   .card {
    //     border: 1px solid #bbb;
    //     padding: 8px;
    //     background: #fafafa;
    //   }

    //   .grid {
    //     display: flex;
    //     gap: 10px;
    //     margin-top: 10px;
    //   }

    //   .result-box {
    //     margin-top: 10px;
    //     border: 2px solid #007cff;
    //     background: #eef6ff;
    //     padding: 10px;
    //     font-size: 11.5px;
    //   }

    //   .images {
    //     display: flex;
    //     gap: 10px;
    //     margin-top: 10px;
    //   }

    //   .images img {
    //     max-width: 48%;
    //     border: 1px solid #ccc;
    //   }

    //   .footer {
    //     margin-top: 10px;
    //     border-top: 1px solid #ccc;
    //     padding-top: 6px;
    //     text-align: center;
    //     font-size: 10.5px;
    //     color: #555;
    //     line-height: 1.4;
    //   }
    // </style>

    // </head>

    // <body>
    // <div class="page">

    // <!-- HEADER -->
    // <div style="display:flex;justify-content:space-between;align-items:center;border-bottom:2px solid #007cff;padding-bottom:6px;">
    //   <h2>Duct Calculation Result</h2>
    //   <img src="/logo.svg" style="height:32px"/>
    // </div>

    // <!-- INPUT + CALCULATIONS -->
    // <div class="grid">

    //   <!-- INPUT PARAMETERS -->
    //   <div class="card" style="flex:1">
    //     <h3>Input Parameters</h3>
    //     <table>
    //       <tr><td class="label">Client Name</td><td>${safe(formData.clientName)}</td></tr>
    //       <tr><td class="label">UV Lamp Size</td><td>${safe(formData.uvSize, "mm")}</td></tr>
    //       <tr><td class="label">Duct Width</td><td>${safe(formData.width, "mm")}</td></tr>
    //       <tr><td class="label">Duct Length</td><td>${safe(formData.length, "mm")}</td></tr>
    //       <tr><td class="label">Flow Rate</td><td>${safe(formData.cfm, "CFM")}</td></tr>
    //       <tr><td class="label">Lamp Length</td><td>${safe(formData.lampLength, "mm")}</td></tr>
    //       <tr><td class="label">Number of Rows</td><td>${safe(formData.numRows, "Nos")}</td></tr>
    //       <tr><td class="label">Number of Columns</td><td>${safe(formData.numCols, "Nos")}</td></tr>
    //       <tr><td class="label">Total Lamp Wattage</td><td>${safe(formData.totalWattage, "W")}</td></tr>
    //       <tr><td class="label">UV Intensity @1m</td><td>${safe(formData.at1mWcm2Input, "mW/cm²")}</td></tr>
    //       <tr><td class="label">UV Intensity @x</td><td>${safe(formData.at450mWcm2Input, "mW/cm²")}</td></tr>
    //     </table>
    //   </div>

    //   <!-- DETAILED CALCULATIONS -->
    //   ${showCalc1 ? `
    //   <div class="card" style="flex:1">

    //     <h3>Derived Flow & Exposure Calculations</h3>
    //     <table>
    //       <tr><td class="label">Duct Area</td><td>${safe(calculations.ductAreaMm2, "mm²")}</td></tr>
    //       <tr><td class="label">Duct Area</td><td>${safe(calculations.ductAreaM2, "m²")}</td></tr>
    //       <tr>
    //         <td class="label">Volumetric Flow Rate</td>
    //         <td>
    //           ${safe(calculations.m3Hr, "m³/hr")} |
    //           ${safe(calculations.m3Min, "m³/min")} |
    //           ${safe(calculations.m3Sec, "m³/sec")}
    //         </td>
    //       </tr>
    //       <tr><td class="label">Flow Velocity</td><td>${safe(calculations.flowVelocity, "m/s")}</td></tr>
    //       <tr><td class="label">Exposure Time</td><td>${safe(calculations.exposureTime, "sec")}</td></tr>
    //     </table>

    //     <h3 style="margin-top:10px;">UV Dose & Lamp Requirement Calculations</h3>
    //     <table>
    //       <tr>
    //         <td class="label">UV Intensity @1 Meter</td>
    //         <td>
    //           ${safe(calculations.at1mWcm2, "mW/cm²")} |
    //           ${safe(calculations.at1mWm2, "W/m²")}
    //         </td>
    //       </tr>
    //       <tr><td class="label">Required Intensity (D90)</td><td>${safe(calculations.at1mIreqD90Wm2, "W/m²")}</td></tr>
    //       <tr><td class="label">No. of Lamps Required (D90)</td><td>${safe(calculations.at1mNoOfLampsD90, "Nos")}</td></tr>
    //       <tr><td class="label">Required Intensity (D99)</td><td>${safe(calculations.at1mIreqD99Wm2, "W/m²")}</td></tr>
    //       <tr><td class="label">No. of Lamps Required (D99)</td><td>${safe(calculations.at1mNoOfLampsD99, "Nos")}</td></tr>
    //     </table>

    //   </div>
    //   ` : ""}

    // </div>

    // <!-- SIMULATION RESULT -->
    // <div class="result-box">
    //   <h3>Simulation Results</h3>
    //   <p><b>Corrected UV Dosage:</b> ${safe(calculations.correctedUVD, "µW/cm²·sec")}</p>
    //   <p><b>Corrected UV Dosage (J/m²):</b> ${(calculations.correctedUVD * 0.01 || 0).toFixed(2)}</p>
    //   <p><b>Minimum UV Intensity at Point “d”:</b> ${safe(calculations.minimumIntAtD, "µW/cm²")}</p>
    //   <p><b>UV Intensity at Point “a”:</b> ${safe(calculations.intensityAtA, "µW/cm²")}</p>
    //   <p><b>UV Intensity at Point “b”:</b> ${safe(calculations.intensityAtB, "µW/cm²")}</p>
    // </div>

    // <!-- DIAGRAMS -->
    // ${showDiagrams ? `
    // <div class="images">
    //   <img src="${layoutImg}" />
    //   <img src="${heatmapImg}" />
    // </div>
    // ` : ""}

    // <!-- FOOTER -->
    // <div style="text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 12px; color: #666; line-height: 1.6;">
    //                     <div style="font-weight: bold; font-size: 14px; color: #007cff; margin-bottom: 10px;">
    //                         ULTRAPURE ENVIROCARE PRIVATE LIMITED<br>
    //                         (FORMERLY ULTRAFRESH MARKETING PVT LTD)
    //                     </div>
    //                     <div style="margin-bottom: 10px;">
    //                         <strong>CIN: U51909DL2003PTC120170</strong>
    //                     </div>
    //                     <div>
    //                         <strong>Registered Office:</strong> CB-344, 2nd Floor, Ring road, Naraina, New Delhi – 110028,<br>
    //                         <strong>Email:</strong> <span style="color: #007cff;">info@ultrapureindia.in</span> |
    //                         <strong>Website:</strong> <span style="color: #007cff;">www.ultrapureindia.in</span> |
    //                         <strong>Toll Free No.:</strong> 1800 11 00 50.<br>
    //                         <strong>Telephone:</strong> +91 7428534141.<br>
    //                         <strong>Branch Locations:</strong> Delhi | Kolkata | Nagpur | Ahmedabad | Bangluru | Chennai | Pune | Hyderabad.
    //                     </div>
    //                 </div>

    // </div>
    // </body>
    // </html>
    // `;
    //     };

    // const buildPdfHtml = () => {
    //         const showDiagrams = includeDiagrams === "yes";

    //         const layoutImg = showDiagrams ? canvasToImage(layoutCanvasRef) : "";
    //         const heatmapImg = showDiagrams ? canvasToImage(uvCanvasRef) : "";

    //         const v = (x) => (x !== undefined && x !== null ? x : "");

    //         return `
    // <!DOCTYPE html>
    // <html>
    // <head>
    // <meta charset="UTF-8">
    // <title>Duct-Results</title>

    // <style>
    //   body {
    //     margin: 0;
    //     padding: 0;
    //     background: #fff;
    //     font-family: "Times New Roman", serif;
    //     border: 3px solid #000;
    //   }

    //   .page {
    //     width: 744px;
    //     height: 900px;
    //     margin: auto;
    //     box-sizing: border-box;
    //     padding: 5px 14px;
    //     border: 3px solid #000;
    //   }



    //   .title {
    //     text-align: center;
    //     font-weight: bold;
    //     font-size: 15px;
    //     margin-bottom: 6px;
    //   }

    //   /* Two-column section */
    //   .columns {
    //     display: flex;
    //     font-size: 12px;
    //     line-height: 1.25;
    //     margin:20px 0;
    //   }

    //   .left, .right {
    //     width: 50%;
    //   }

    //   .row {
    //     margin-bottom: 4px;
    //     white-space: nowrap;
    //   }

    //   .label {
    //     display: inline-block;
    //     width: 235px;
    //     font-weight: bold;
    //   }

    //   /* Divider */
    //   .divider {
    //     border-top: 1px solid #aaa;
    //     margin: 8px 0;
    //   }

    //   .result{
    //     margin-top: 4px;
    //     margin-bottom: 20px;
    //     }
    //   /* Result text */
    //   .results p {
    //     margin: 2px 0;
    //     font-size: 12px;
    //   }

    //   /* Diagrams */
    //   .diagrams {
    //     display: flex;
    //     gap: 25px;
    //     margin-top: 20px;
    //     margin-left: auto;
    //   }

    //   .diagrams img {
    //     width: 48%;
    //     gap:10px;
    //     border: 1px solid #000;
    //   }

    //   /* Footer */
    //  .pdf-footer {
    //   margin-top: 6px;
    //   padding: 10px 12px;
    //   text-align: center;
    //   color: #ffffff;
    //   font-family: Arial, Helvetica, sans-serif;

    //   background: linear-gradient(
    //     to right,
    //     #1aa7c9 0%,
    //     #5bb6a6 50%,
    //     #8bc34a 100%
    //   );
    // }

    // .footer-title {
    //   font-size: 17px;
    //   letter-spacing: 0.4px;
    //   margin-bottom: 4px;
    // }

    // .footer-text {
    //   font-size: 10.5px;
    //   line-height: 1.35;
    //   margin-bottom: 4px;
    // }

    // .footer-contact {
    //   font-size: 10.5px;
    //   display: flex;
    //   justify-content: center;
    //   gap: 6px;
    //   flex-wrap: wrap;
    // }

    // </style>
    // </head>

    // <body>
    // <div style="display:flex;justify-content:end;align-items:center; padding-bottom:6px;">
    //   <img src="/logo.svg" style="height:32px"/>
    // </div>
    // <div class="page">
    //   <div class="title">Duct-Results</div>

    //   <div class="columns">

    //     <!-- LEFT COLUMN -->
    //     <div class="left">
    //       <div class="row"><span class="label">Client Name</span>: ${v(formData.clientName)}</div>
    //       <div class="row"><span class="label">UV Lamp Size(mm)</span>: ${v(formData.uvSize)}</div>
    //       <div class="row"><span class="label">Duct Width (mm)</span>: ${v(formData.width)}</div>
    //       <div class="row"><span class="label">Duct Length</span>: ${v(formData.length)}</div>
    //       <div class="row"><span class="label">Flow rate</span>: ${v(formData.cfm)} cfm</div>
    //       <div class="row"><span class="label">Lamp Length</span>: ${v(formData.lampLength)} mm</div>
    //       <div class="row"><span class="label">Number of Rows</span>: ${v(formData.numRows)} Nos</div>
    //       <div class="row"><span class="label">Number of columns</span>: ${v(formData.numCols)} Nos</div>
    //       <div class="row"><span class="label">Total Lamp Wattage</span>: ${v(formData.totalWattage)} Watt</div>
    //       <div class="row"><span class="label">UV Intensity at 1m (mW/cm2)</span>: ${v(formData.at1mWcm2Input)}</div>
    //       <div class="row"><span class="label">UV Intensity at "x" mm (mW/cm2)</span>: ${v(formData.at450mWcm2Input)}</div>
    //     </div>

    //     <!-- RIGHT COLUMN -->
    //     <div class="right">
    //       <div class="row"><span class="label">Duct Area(mm2)</span>: ${v(calculations.ductAreaMm2)}</div>
    //       <div class="row"><span class="label">Duct Area(m2)</span>: ${v(calculations.ductAreaM2)}</div>
    //       <div class="row"><span class="label">Vol.Flow Rate(m3/sec)</span>: ${v(calculations.m3Sec)}</div>
    //       <div class="row"><span class="label">Flow Velocity (m/s)</span>: ${v(calculations.flowVelocity)}</div>
    //       <div class="row"><span class="label">Exposure Time (s)</span>: ${v(calculations.exposureTime)}</div>

    //       <div class="row"><span class="label">UV Intensity at 1m(mW/cm2)</span>: ${v(calculations.at1mWcm2)}</div>
    //       <div class="row"><span class="label">UV Intensity at 1m(W/m2)</span>: ${v(calculations.at1mWm2)}</div>
    //       <div class="row"><span class="label">UV Intensity at 1m [I req. (D90)] [W/m2]</span>: ${v(calculations.at1mIreqD90Wm2)}</div>
    //       <div class="row"><span class="label">UV Intensity at 1m (No. of Lamps.)</span>: ${v(calculations.at1mNoOfLampsD90)}</div>
    //       <div class="row"><span class="label">UV Intensity at 1m I req. (D99) [W/m2]</span>: ${v(calculations.at1mIreqD99Wm2)}</div>
    //       <div class="row"><span class="label">UV Intensity at 1m (No. of Lamps.)</span>: ${v(calculations.at1mNoOfLampsD99)}</div>

    //       <div class="row"><span class="label">UV Intensity at "x" mm(mW/cm2)</span>: ${v(calculations.at450mWcm2)}</div>
    //       <div class="row"><span class="label">UV Intensity at "x" mm(W/m2)</span>: ${v(calculations.at450mmWm2)}</div>
    //       <div class="row"><span class="label">UV Intensity at "x" mm [I req. (D90)] [W/m2]</span>: ${v(calculations.iReqD90Wm2)}</div>
    //       <div class="row"><span class="label">UV Intensity at "x" mm (No. of Lamps.)</span>: ${v(calculations.noOfLampsD90)}</div>
    //       <div class="row"><span class="label">UV Intensity at "x" mm I req. (D99) [W/m2]</span>: ${v(calculations.iReqD99Wm2)}</div>
    //       <div class="row"><span class="label">UV Intensity at "x" mm (No. of Lamps.)</span>: ${v(calculations.noOfLampsD99)}</div>
    //     </div>

    //   </div>

    //   <div class="divider"></div>

    //   <div class="results">
    //     <p>Your Corrected UV Dosage is <b>${v(calculations.correctedUVD)}</b> microwatt/ sq.cm./sec.</p>
    //     <p>Your Corrected UV Dosage in J/m2 is <b>${(calculations.correctedUVD * 0.01).toFixed(2)}</b></p>
    //     <p>Your Minimum Intensity at "d" is <b>${v(calculations.minimumIntAtD)}</b> microwatt/ sq.cm.</p>
    //     <p>Your Intensity at "a" is <b>${v(calculations.intensityAtA)}</b> microwatt/ sq.cm.</p>
    //     <p>Your Intensity at "b" is <b>${v(calculations.intensityAtB)}</b> microwatt/ sq.cm.</p>
    //   </div>

    //   ${showDiagrams ? `
    //   <div class="diagrams">
    //     <img src="${layoutImg}">
    //     <img src="${heatmapImg}">
    //   </div>
    //   ` : ""}



    // </div>

    // <div class="pdf-footer">
    //   <div class="footer-title">
    //     ULTRAPURE ENVIROCARE PRIVATE LIMITED
    //   </div>

    //   <div class="footer-text">
    //     Head Office: 3rd Floor, Ion House, Dr. E. Mose Road, Mahalakshmi, Mumbai-400011<br>
    //     Registered Office: CB-344, 2nd Floor, Ring Road, Naraina, New Delhi-110028
    //   </div>

    //   <div class="footer-contact">
    //     <span>info@ultrapureindia.in</span>
    //     <span>|</span>
    //     <span>www.ultrapureindia.in</span>
    //     <span>|</span>
    //     <span>1800-1100-50</span>
    //   </div>
    // </div>

    // </body>
    // </html>
    // `;
    //     };


    const buildPdfHtml = () => {
        const showCalc1 = reportType !== "withoutCalc1";
        const showDiagrams = includeDiagrams === "yes";

        const layoutImg = showDiagrams ? canvasToImage(layoutCanvasRef) : "";
        const heatmapImg = showDiagrams ? canvasToImage(uvCanvasRef) : "";

        const v = (x) => (x !== undefined && x !== null && x !== "" ? x : "");

        return `
<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>Duct-Results</title>
<link href="https://fonts.googleapis.com/css2?family=Tinos:wght@400;700&display=swap" rel="stylesheet">

<style>
  body {
    margin: 0;
    padding: 0;
    background: #fff;
    font-family: "Tinos", serif;
  }

  .page {
    width: 744px;
    margin: auto;
    height:820px;
    padding: 5px 14px;
    border: 3px solid #000;
    box-sizing: border-box;
    margin-top:30px;
    margin-bottom:40px;
  }


  .title {
    text-align: center;
    font-weight: bold;
    font-size: 15px;
    margin-bottom: 6px;
  }

  .columns {
    display: flex;
    font-size: 12px;
    line-height: 1.25;
    margin: 20px 0;
  }

  .left, .right {
    width: 50%;
  }

  .row {
    margin-bottom: 4px;
    white-space: nowrap;
  }

  .label {
    display: inline-block;
    width: 235px;
    font-weight: bold;
  }

  .divider {
    border-top: 1px solid #aaa;
    margin: 8px 0;
  }

  .results p {
    margin: 2px 0;
    font-size: 12px;
  }

  .diagrams {
    display: flex;
    gap: 25px;
    margin-top: 40px;
  }

  .diagrams img {
    width: 48%;
    border: 1px solid #000;
  }

  .pdf-footer {
    margin-top: 6px;
    margin-bottom: 6px;
    padding: 10px 12px;
    text-align: center;
    color: #ffffff;
    font-family: Arial, Helvetica, sans-serif;
    background: linear-gradient(
      to right,
      #1aa7c9 0%,
      #5bb6a6 50%,
      #8bc34a 100%
    );
  }

  .footer-title {
    font-size: 17px;
    margin-bottom: 4px;
  }

  .footer-text {
    font-size: 10.5px;
    line-height: 1.35;
    margin-bottom: 4px;
  }

  .footer-contact {
    font-size: 10.5px;
    margin-bottom: 15px;
  }
</style>
</head>

<body>

<div style="display:flex;justify-content:end;padding-bottom:6px;">
  <img src="/logo.svg" style="height:60px"/>
</div>

<div class="page">
  <div class="title">Duct-Results</div>

  <div class="columns">

    <!-- LEFT COLUMN : INPUT PARAMETERS (UNCHANGED) -->
    <div class="left">
      <div class="row"><span class="label">Client Name</span>: ${v(formData.clientName)}</div>
      <div class="row"><span class="label">UV Lamp Size(mm)</span>: ${v(formData.uvSize)}</div>
      <div class="row"><span class="label">Duct Width (mm)</span>: ${v(formData.width)}</div>
      <div class="row"><span class="label">Duct Length</span>: ${v(formData.length)}</div>
      <div class="row"><span class="label">Flow rate</span>: ${v(formData.cfm)} cfm</div>
      <div class="row"><span class="label">Lamp Length</span>: ${v(formData.lampLength)} mm</div>
      <div class="row"><span class="label">Number of Rows</span>: ${v(formData.numRows)} Nos</div>
      <div class="row"><span class="label">Number of columns</span>: ${v(formData.numCols)} Nos</div>
      <div class="row"><span class="label">Total Lamp Wattage</span>: ${v(formData.totalWattage)} Watt</div>
      <div class="row"><span class="label">UV Intensity at 1m (mW/cm2)</span>: ${v(formData.at1mWcm2Input)}</div>
      <div class="row"><span class="label">UV Intensity at "x" mm (mW/cm2)</span>: ${v(formData.at450mWcm2Input)}</div>
    </div>

    <!-- RIGHT COLUMN : CALCULATION 1 (CONDITIONAL, FIELDS UNCHANGED) -->
    ${showCalc1 ? `
    <div class="right">
      <div class="row"><span class="label">Duct Area(mm2)</span>: ${v(calculations.ductAreaMm2)}</div>
      <div class="row"><span class="label">Duct Area(m2)</span>: ${v(calculations.ductAreaM2)}</div>
      <div class="row"><span class="label">Vol.Flow Rate(m3/sec)</span>: ${v(calculations.m3Sec)}</div>
      <div class="row"><span class="label">Flow Velocity (m/s)</span>: ${v(calculations.flowVelocity)}</div>
      <div class="row"><span class="label">Exposure Time (s)</span>: ${v(calculations.exposureTime)}</div>

      <div class="row"><span class="label">UV Intensity at 1m(mW/cm2)</span>: ${v(calculations.at1mWcm2)}</div>
      <div class="row"><span class="label">UV Intensity at 1m(W/m2)</span>: ${v(calculations.at1Wcm2)}</div>
      <div class="row"><span class="label">UV Intensity at 1m [I req. (D90)] [W/m2]</span>: ${v(calculations.at1mIreqD90Wm2)}</div>
      <div class="row"><span class="label">UV Intensity at 1m (No. of Lamps.)</span>: ${v(calculations.at1mNoOfLampsD90)}</div>
      <div class="row"><span class="label">UV Intensity at 1m I req. (D99) [W/m2]</span>: ${v(calculations.at1mIreqD99Wm2)}</div>
      <div class="row"><span class="label">UV Intensity at 1m (No. of Lamps.)</span>: ${v(calculations.at1mNoOfLampsD99)}</div>

      <div class="row"><span class="label">UV Intensity at "x" mm(mW/cm2)</span>: ${v(calculations.at450mWcm2)}</div>
      <div class="row"><span class="label">UV Intensity at "x" mm(W/m2)</span>: ${v(calculations.at450mmWm2)}</div>
      <div class="row"><span class="label">UV Intensity at "x" mm [I req. (D90)] [W/m2]</span>: ${v(calculations.iReqD90Wm2)}</div>
      <div class="row"><span class="label">UV Intensity at "x" mm (No. of Lamps.)</span>: ${v(calculations.noOfLampsD90)}</div>
      <div class="row"><span class="label">UV Intensity at "x" mm I req. (D99) [W/m2]</span>: ${v(calculations.iReqD99Wm2)}</div>
      <div class="row"><span class="label">UV Intensity at "x" mm (No. of Lamps.)</span>: ${v(calculations.noOfLampsD99)}</div>
    </div>
    ` : ``}

  </div>

  <div class="divider"></div>

  <!-- CALCULATION 2 : ALWAYS SHOWN -->
  <div class="results">
    <div class="title" style="text-decoration:underline; text-align:left">Simulation Results</div>
    <p>Your Corrected UV Dosage is <b>${v(calculations.correctedUVD)}</b> microwatt/ sq.cm./sec.</p>
    <p>Your Corrected UV Dosage in J/m2 is <b>${(calculations.correctedUVD * 0.01).toFixed(2)}</b></p>
    <p>Your Minimum Intensity at "d" is <b>${v(calculations.minimumIntAtD)}</b> microwatt/ sq.cm.</p>
    <p>Your Intensity at "a" is <b>${v(calculations.intensityAtA)}</b> microwatt/ sq.cm.</p>
    <p>Your Intensity at "b" is <b>${v(calculations.intensityAtB)}</b> microwatt/ sq.cm.</p>
  </div>

  ${showDiagrams ? `
  <div class="diagrams">
    <img src="${layoutImg}" style="padding:10px;">
    <img src="${heatmapImg}">
  </div>
  ` : ""}

</div>

<div class="pdf-footer">
  <div class="footer-title">ULTRAPURE ENVIROCARE PRIVATE LIMITED</div>
  <div class="footer-text">
    Head Office: 3rd Floor, Ion House, Dr. E. Mose Road, Mahalakshmi, Mumbai-400011<br>
    Registered Office: CB-344, 2nd Floor, Ring Road, Naraina, New Delhi-110028
  </div>
  <div class="footer-contact">
    info@ultrapureindia.in | www.ultrapureindia.in | 1800-1100-50
  </div>
</div>

</body>
</html>
`;
    };


    const handleDownloadPDF = () => {
        const pdfHtml = buildPdfHtml();

        const container = document.createElement("div");
        container.innerHTML = pdfHtml;
        document.body.appendChild(container);

        html2pdf()
            .set({
                filename: `Duct-Report-${reportType}-${includeDiagrams}.pdf`,
                margin: 6,
                image: { type: "jpeg", quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true },
                jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
            })
            .from(container)
            .save()
            .then(() => {
                document.body.removeChild(container);
            });
    };


    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            {/* ================= HEADER ================= */}
            <nav className="bg-white shadow-sm border-b">
                <div className="container mx-auto px-6 py-4 flex justify-between items-center">
                    <h1 className="text-xl font-semibold text-blue-700">
                        UVGI Duct Calculator
                    </h1>
                    <Link to="/ahu_cal" className="text-gray-600 hover:text-gray-800">
                        <button className="px-4 py-2 border rounded hover:bg-gray-50">
                            AHU Calculator
                        </button>
                    </Link>
                </div>
            </nav>

            {/* Main Content */}
            <div className="container mx-auto px-4 py-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column - Input Form */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-lg shadow">
                            <div className="bg-blue-50 px-6 py-4 border-b">
                                <h3 className="text-lg font-semibold text-blue-700">User Input</h3>
                            </div>
                            <div className="p-6">
                                <form className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
                                        <input
                                            type="text"
                                            name="clientName"
                                            value={formData.clientName}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">UV Lamp Size (mm)</label>
                                        <input
                                            type="text"
                                            name="uvSize"
                                            value={formData.uvSize}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <span className="text-xs text-gray-500">in cm: {conversions.uvSizeInCm}</span>
                                    </div>

                                    <div className="pt-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Duct Size (mm):</label>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Width</label>
                                        <input
                                            type="text"
                                            name="width"
                                            value={formData.width}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <span className="text-xs text-gray-500">in cm: {conversions.widthInCm}</span>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Length</label>
                                        <input
                                            type="text"
                                            name="length"
                                            value={formData.length}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <span className="text-xs text-gray-500">in cm: {conversions.lengthInCm}</span>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">CFM</label>
                                        <input
                                            type="text"
                                            name="cfm"
                                            value={formData.cfm}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Lamp Length</label>
                                        <input
                                            type="text"
                                            name="lampLength"
                                            value={formData.lampLength}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <span className="text-xs text-gray-500">in cm: {conversions.lampLengthInCm}</span>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Number of Rows</label>
                                        <input
                                            type="text"
                                            name="numRows"
                                            value={formData.numRows}
                                            onChange={handleInputChange}
                                            placeholder="of lamps"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Number of Columns</label>
                                        <input
                                            type="text"
                                            name="numCols"
                                            value={formData.numCols}
                                            onChange={handleInputChange}
                                            placeholder="of lamps"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Total Lamp Wattage</label>
                                        <input
                                            type="text"
                                            name="totalWattage"
                                            value={formData.totalWattage}
                                            onChange={handleInputChange}
                                            placeholder="in W"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div className="pt-2">
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Intensity of UV lamp:</label>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">at 1 meter (mW/cm²)</label>
                                        <input
                                            type="text"
                                            name="at1mWcm2Input"
                                            value={formData.at1mWcm2Input}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">at "x" mm (mW/cm²)</label>
                                        <input
                                            type="text"
                                            name="at450mWcm2Input"
                                            value={formData.at450mWcm2Input}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div className="flex gap-3 pt-4">
                                        <button
                                            type="button"
                                            onClick={handleReset}
                                            className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition"
                                        >
                                            Return to selection
                                        </button>
                                        <button
                                            type="button"
                                            onClick={calculate}
                                            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
                                        >
                                            Submit
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Calculations */}
                    <div className="lg:col-span-2 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                            {/* Calculations 1 */}
                            <div className="bg-white rounded-lg shadow">
                                <div className="bg-green-50 px-6 py-4 border-b">
                                    <h3 className="text-lg font-semibold text-green-700">
                                        Calculations: <span className="text-red-600">1</span>
                                    </h3>
                                </div>
                                <div className="p-6">
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Duct Area (mm²)</label>
                                            <input
                                                type="text"
                                                value={calculations.ductAreaMm2}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Duct Area (m²)</label>
                                            <input
                                                type="text"
                                                value={calculations.ductAreaM2}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div className="col-span-2 md:col-span-3">
                                            <label className="block text-sm font-bold text-gray-900 mb-2">Vol. Flow Rate:</label>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">m³/hr</label>
                                            <input
                                                type="text"
                                                value={calculations.m3Hr}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">m³/min</label>
                                            <input
                                                type="text"
                                                value={calculations.m3Min}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">m³/sec</label>
                                            <input
                                                type="text"
                                                value={calculations.m3Sec}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Flow Velocity (m/s)</label>
                                            <input
                                                type="text"
                                                value={calculations.flowVelocity}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Exposure Time (s)</label>
                                            <input
                                                type="text"
                                                value={calculations.exposureTime}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div className="col-span-2 md:col-span-3">
                                            <label className="block text-sm font-bold text-gray-900 mb-2">Intensity of UV lamp:</label>
                                            <p className="text-sm text-gray-600 mb-2 underline">at 1 meter</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">mW/cm²</label>
                                            <input
                                                type="text"
                                                value={calculations.at1mWcm2}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">W/m²</label>
                                            <input
                                                type="text"
                                                value={calculations.at1Wcm2}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">I req. (D90) [W/m²]</label>
                                            <input
                                                type="text"
                                                value={calculations.at1mIreqD90Wm2}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">No. of Lamps</label>
                                            <input
                                                type="text"
                                                value={calculations.at1mNoOfLampsD90}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">I req. (D99) [W/m²]</label>
                                            <input
                                                type="text"
                                                value={calculations.at1mIreqD99Wm2}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">No. of Lamps</label>
                                            <input
                                                type="text"
                                                value={calculations.at1mNoOfLampsD99}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div className="col-span-2 md:col-span-3">
                                            <p className="text-sm text-gray-600 mb-2 underline">at "x" mm</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">mW/cm²</label>
                                            <input
                                                type="text"
                                                value={calculations.at450mWcm2}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">W/m²</label>
                                            <input
                                                type="text"
                                                value={calculations.at450mmWm2}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">I req. (D90) [W/m²]</label>
                                            <input
                                                type="text"
                                                value={calculations.iReqD90Wm2}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">No. of Lamps</label>
                                            <input
                                                type="text"
                                                value={calculations.noOfLampsD90}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">I req. (D99) [W/m²]</label>
                                            <input
                                                type="text"
                                                value={calculations.iReqD99Wm2}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">No. of Lamps</label>
                                            <input
                                                type="text"
                                                value={calculations.noOfLampsD99}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Calculations 2 */}
                            <div className="bg-white rounded-lg shadow">
                                <div className="bg-green-50 px-6 py-4 border-b">
                                    <h3 className="text-lg font-semibold text-green-700">
                                        Calculations: <span className="text-red-600">2</span>
                                    </h3>
                                </div>
                                <div className="p-6">
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Corrected UV Dosage <span className="text-gray-500">(in microwatt/sq.cm./sec)</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={calculations.correctedUVD}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Minimum Intensity at 'd' <span className="text-gray-500">(in microwatt/sq.cm)</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={calculations.minimumIntAtD}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Intensity at 'a' <span className="text-gray-500">(in microwatt/sq.cm)</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={calculations.intensityAtA}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Intensity at 'b' <span className="text-gray-500">(in microwatt/sq.cm)</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={calculations.intensityAtB}
                                                readOnly
                                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* PDF Download Section */}
                                <div className="border-t px-6 py-4">
                                    <div className="space-y-4">
                                        <label className="block text-sm font-semibold text-gray-700">PDF Download:</label>
                                        <hr className="my-3" />

                                        <div className="space-y-4">
                                            <div>
                                                <p className="text-sm font-semibold text-gray-700 mb-2">Full Report</p>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <label className="flex items-center space-x-2 cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            name="fullReport"
                                                            value="yes"
                                                            checked={reportType === 'full' && includeDiagrams === 'yes'}
                                                            onChange={() => {
                                                                setReportType('full');
                                                                setIncludeDiagrams('yes');
                                                            }}
                                                            className="w-4 h-4 text-blue-600"
                                                        />
                                                        <span className="text-sm">All <strong>With Diagrams</strong></span>
                                                    </label>
                                                    <label className="flex items-center space-x-2 cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            name="fullReport"
                                                            value="no"
                                                            checked={reportType === 'full' && includeDiagrams === 'no'}
                                                            onChange={() => {
                                                                setReportType('full');
                                                                setIncludeDiagrams('no');
                                                            }}
                                                            className="w-4 h-4 text-blue-600"
                                                        />
                                                        <span className="text-sm">All <strong>Without Diagrams</strong></span>
                                                    </label>
                                                </div>
                                            </div>

                                            <div>
                                                <p className="text-sm font-semibold text-gray-700 mb-2">Without Calculation 1</p>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <label className="flex items-center space-x-2 cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            name="calc1Report"
                                                            value="yes"
                                                            checked={reportType === 'withoutCalc1' && includeDiagrams === 'yes'}
                                                            onChange={() => {
                                                                setReportType('withoutCalc1');
                                                                setIncludeDiagrams('yes');
                                                            }}
                                                            className="w-4 h-4 text-blue-600"
                                                        />
                                                        <span className="text-sm">With Diagrams</span>
                                                    </label>
                                                    <label className="flex items-center space-x-2 cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            name="calc1Report"
                                                            value="no"
                                                            checked={reportType === 'withoutCalc1' && includeDiagrams === 'no'}
                                                            onChange={() => {
                                                                setReportType('withoutCalc1');
                                                                setIncludeDiagrams('no');
                                                            }}
                                                            className="w-4 h-4 text-blue-600"
                                                        />
                                                        <span className="text-sm">Without Diagrams</span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>

                                        <button
                                            onClick={handleDownloadPDF}
                                            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition mt-4"
                                        >
                                            Download PDF
                                        </button>
                                    </div>
                                </div>
                                {/* Visualizations */}
                                <div className="space-y-6 p-4 max-w-[450px]">
                                    <div>
                                        <h4 className="text-xl font-semibold text-gray-800 mb-3">UVGI System In Duct</h4>
                                        <canvas
                                            ref={layoutCanvasRef}
                                            width="485"
                                            height="250"
                                            className="border border-gray-300 w-full"
                                        />
                                    </div>

                                    <div>
                                        <h4 className="text-xl font-semibold text-gray-800 mb-3">UV Intensity Heatmap</h4>
                                        <canvas
                                            ref={uvCanvasRef}
                                            width="500"
                                            height="350"
                                            className="border border-gray-300 w-full"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>


                    </div>
                </div>
            </div>
        </div>
    );
};

export default DuctCalculator2;